/**
 * 用户通用功能
 */
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { post } from '@/utils/request';

import Utils from '@/utils/utils';
import RtcClient from '@/lib/RtcClient';
import {
  clearCurrentMsg,
  clearHistoryMsg,
  localJoinRoom,
  localLeaveRoom,
  updateAIGCState,
} from '@/store/slices/room';

import useRtcListeners from '@/lib/listenerHooks';
import { RootState } from '@/store';

import {
  updateMediaInputs,
  updateSelectedDevice,
  setDevicePermissions,
} from '@/store/slices/device';
import { resetConfig } from '@/store/slices/stream';
import AIGCConfig from '@/config';
import { requestGetMethod } from '@/app/base';

export interface FormProps {
  username: string;
  roomId: string;
  publishAudio?: boolean;
}

/**
 * 处理用户设备权限
 */
export const useGetDevicePermission = () => {
  const [permission, setPermission] = useState<{
    audio: boolean;
  }>();

  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      const permission = await RtcClient.checkPermission();
      dispatch(setDevicePermissions(permission));
      setPermission(permission);
    })();
  }, [dispatch]);
  return permission;
};

/**
 * 用户加入房间
 */
export const useJoin = (): [
  boolean,
  (formValues: FormProps, fromRefresh: boolean) => Promise<void | boolean>
] => {
  const devicePermissions = useSelector((state: RootState) => state.device.devicePermissions);
  const room = useSelector((state: RootState) => state.room);
  const dispatch = useDispatch();
  const [joining, setJoining] = useState(false);
  const listeners = useRtcListeners();
  const navigate = useNavigate();

  /**
   * 处理aigc模式
   * @param roomId
   * @param userId
   */
  const handleAIGCModeStart = async (roomId: string, userId: string) => {
    //开启新会话
    post("/cbc/conversation/ops/start",{
      roomId: roomId,
      userName: userId,
    }).then((res)=>{
      console.log(res)
      //保存会话ID
      let conversationId = res.data;
      localStorage.setItem("conversationId",conversationId);
    })

    //开启会话
    if (room.isAIGCEnable) {
      await RtcClient.stopAudioBot(roomId, userId);
      dispatch(clearCurrentMsg());
      await RtcClient.startAudioBot(roomId, userId);
    } else {
      await RtcClient.startAudioBot(roomId, userId);
    }
    dispatch(updateAIGCState({ isAIGCEnable: true }));
  };

  /**
   * 处理用户加入房间
   * @param formValues
   */
  async function disPatchJoin(formValues: FormProps): Promise<boolean | undefined> {
    if (joining) {
      return;
    }
    setJoining(true);

    const { username, roomId } = formValues;
    console.log(`用户加入房间#username=${username}#roomId=${roomId}`);

    // 服务端根据roomId和userId生成token
    const queryData = await requestGetMethod(`/accessToken?roomId=${roomId}&userId=${username}`)({});
    const res = await queryData?.json();
    console.log('getAccessToken', res.data);
    if (res.code !== 200) {
      message.error('获取签名错误！');
      return;
    }
    const token = res.data;

    //初始化RTC Engine
    await RtcClient.createEngine({
      appId: AIGCConfig.AppId,
      roomId,
      uid: username,
      login_token: token,
    });

    /** Set events callbacks */
    RtcClient.addEventListeners(listeners);

    /** RTC starting to join room */
    await RtcClient.joinRoom(token, username);
    console.log(' ------ userJoinRoom\n', `roomId: ${roomId}\n`, `uid: ${username}`);

    /** Set users' devices info */
    const mediaDevices = await RtcClient.getDevices();

    //开始捕捉用户语音
    if (devicePermissions.audio) {
      await RtcClient.startAudioCapture();
      //设置音量
      RtcClient.setAudioVolume(100);
    }

    //通知用户加入房间
    dispatch(
      localJoinRoom({
        roomId,
        user: {
          username,
          userId: username,
          publishAudio: true,
        },
      }),
    );

    dispatch(
      updateSelectedDevice({
        selectedMicrophone: mediaDevices.audioInputs[0]?.deviceId,
      }),
    );
    dispatch(updateMediaInputs(mediaDevices));

    setJoining(false);

    //设置session
    Utils.setSessionInfo({
      username,
      roomId,
      publishAudio: true,
    });

    //导航到聊天页
    navigate(`/?roomId=${formValues.roomId}&userId=${formValues.username}`);

    //处理aigc模式
    handleAIGCModeStart(formValues.roomId, formValues.username);
  }

  return [joining, disPatchJoin];
};

/**
 * 用户离开
 */
export const useLeave = () => {
  const dispatch = useDispatch();

  return async function() {
    dispatch(localLeaveRoom());
    dispatch(resetConfig());
    dispatch(updateAIGCState({ isAIGCEnable: false }));
    await Promise.all([RtcClient.stopAudioCapture]);
    RtcClient.leaveRoom();
    dispatch(clearHistoryMsg());
    dispatch(clearCurrentMsg());
  };
};
